import { useState, useEffect } from "react";
import { Loader2, Upload, Palette } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { getThemeById } from "@commonqr/shared/config/themes";

interface ProfileThemeSectionProps {
  initialThemeId?: string | null;
  initialThemeImageUrl?: string | null;
  onThemeChange: (themeId: string | null, themeImageUrl: string | null) => void;
}

interface Theme {
  id: string;
  label: string;
  imageUrl: string;
  variant: string;
  industry?: string;
}

export function ProfileThemeSection({
  initialThemeId,
  initialThemeImageUrl,
  onThemeChange,
}: ProfileThemeSectionProps) {
  const { toast } = useToast();
  const initialIndustry = initialThemeId ? (getThemeById(initialThemeId)?.industry ?? "") : "";
  const [selectedThemeId, setSelectedThemeId] = useState<string | null>(initialThemeId ?? null);
  const [currentThemeImageUrl, setCurrentThemeImageUrl] = useState<string | null>(initialThemeImageUrl ?? null);
  const [selectedIndustry, setSelectedIndustry] = useState<string>(initialIndustry);
  const [themeIndustriesList, setThemeIndustriesList] = useState<string[]>([]);
  const [filteredThemes, setFilteredThemes] = useState<Theme[]>([]);
  const [isUploadingTheme, setIsUploadingTheme] = useState(false);

  useEffect(() => {
    fetch('/api/v1/themes/industries')
      .then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then((d: { industries?: string[] }) => {
        const list = d.industries ?? [];
        setThemeIndustriesList(list);
        if (list.length > 0) setSelectedIndustry(prev => (prev && list.includes(prev)) ? prev : list[0]);
      })
      .catch(err => console.error("[ProfileThemeSection] fetch failed:", err));
  }, []);

  useEffect(() => {
    if (!selectedIndustry) return;
    fetch(`/api/v1/themes?industry=${encodeURIComponent(selectedIndustry)}&limit=50`)
      .then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then((d: { themes?: Theme[] }) => setFilteredThemes(d.themes ?? []))
      .catch(err => console.error("[ProfileThemeSection] fetch failed:", err));
  }, [selectedIndustry]);

  const handleThemeChange = (themeId: string | null, themeImageUrl: string | null) => {
    if (themeId !== undefined) setSelectedThemeId(themeId);
    if (themeImageUrl !== undefined) setCurrentThemeImageUrl(themeImageUrl);
    onThemeChange(themeId, themeImageUrl);
  };

  return (
    <section className="border-b border-border/60 p-4 space-y-3">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
          <Palette className="w-4 h-4" />
        </div>
        <div>
          <h3 className="font-semibold text-[13px]">Profile Theme</h3>
          <p className="text-[11px] text-muted-foreground">Background image for your profile.</p>
        </div>
      </div>

      <div>
        <label className="mb-1.5 block text-[11px] font-medium text-muted-foreground" htmlFor="theme-industry">Industry</label>
        <select
          id="theme-industry"
          value={selectedIndustry}
          onChange={(event) => setSelectedIndustry(event.target.value)}
          className="w-full rounded-lg border border-border bg-card px-3 py-2 text-[12px] outline-none transition-colors focus:border-primary text-foreground"
        >
          {themeIndustriesList.map((industry) => (
            <option key={industry} value={industry}>{industry}</option>
          ))}
        </select>
      </div>

      <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-2">
        {filteredThemes.map((theme) => (
          <button
            key={theme.id}
            type="button"
            onClick={() => handleThemeChange(theme.id, theme.imageUrl)}
            className={`w-16 shrink-0 overflow-hidden rounded-lg border-2 bg-card text-left transition-all ${
              selectedThemeId === theme.id ? "border-primary shadow-md" : "border-transparent hover:border-border"
            }`}
            aria-pressed={selectedThemeId === theme.id}
            title={theme.label}
          >
            <img src={`/api/v1/themes/image/${theme.id}`} alt="" loading="lazy" className="h-[90px] w-16 object-cover" />
            <span className="block truncate px-1 py-1.5 text-center text-[10px] font-semibold capitalize">{theme.variant}</span>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-border px-2.5 py-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/5">
          {isUploadingTheme ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          {isUploadingTheme ? "Uploading…" : "Upload background"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            disabled={isUploadingTheme}
            onChange={async (event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
                toast({ title: "Unsupported image", description: "Choose a JPEG, PNG, or WebP file.", variant: "destructive" });
                return;
              }
              if (file.size > 5 * 1024 * 1024) {
                toast({ title: "Image is too large", description: "Custom backgrounds must be 5MB or smaller.", variant: "destructive" });
                return;
              }
              setIsUploadingTheme(true);
              const formData = new FormData();
              formData.append("image", file);
              try {
                const base = import.meta.env.BASE_URL.replace(/\/$/, "");
                const response = await fetch(`${base}/upload/theme-image`, {
                  method: "POST",
                  headers: { Authorization: `Bearer ${localStorage.getItem("commonqr_token") ?? ""}` },
                  body: formData,
                });
                if (!response.ok) throw new Error("Upload failed");
                const result = await response.json() as { themeImageUrl?: string; url?: string };
                const uploadedUrl = result.themeImageUrl ?? result.url;
                if (!uploadedUrl) throw new Error("No image URL in response");
                handleThemeChange(null, uploadedUrl);
                toast({ title: "Background uploaded", description: "Your custom theme image is ready to save." });
              } catch (error) {
                toast({ title: "Upload failed", description: error instanceof Error ? error.message : "Please try again.", variant: "destructive" });
              } finally {
                setIsUploadingTheme(false);
              }
            }}
          />
        </label>
        {currentThemeImageUrl && (
          <button
            type="button"
            onClick={() => handleThemeChange(selectedThemeId, null)}
            className="text-[11px] font-semibold text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            Remove
          </button>
        )}
      </div>

      {currentThemeImageUrl && (
        <div className="overflow-hidden rounded-lg border border-border">
          <img src={currentThemeImageUrl} alt="Custom profile background preview" className="h-14 w-full object-cover" />
        </div>
      )}
    </section>
  );
}
